"use client";

import { useEffect, useState } from "react";

type FavoriteLocation = { locationSlug: string };

export function useFavoriteLocations() {
  const [favorites, setFavorites] = useState<Set<string>>(new Set());
  const [authenticated, setAuthenticated] = useState<boolean>();
  const [pending, setPending] = useState<string>();

  useEffect(() => {
    const controller = new AbortController();
    void fetch("/api/tables/account/favorites", {
      cache: "no-store",
      signal: controller.signal,
    }).then(async (response) => {
      if (response.status === 401) {
        setAuthenticated(false);
        return;
      }
      if (!response.ok) return;
      const payload = (await response.json()) as { data: FavoriteLocation[] };
      setFavorites(new Set(payload.data.map((favorite) => favorite.locationSlug)));
      setAuthenticated(true);
    }).catch(() => undefined);
    return () => controller.abort();
  }, []);

  const toggle = async (locationSlug: string) => {
    if (authenticated === false) {
      window.location.assign("/account/reservations");
      return;
    }
    const removing = favorites.has(locationSlug);
    setPending(locationSlug);
    try {
      const response = await fetch(
        `/api/tables/account/favorites/${encodeURIComponent(locationSlug)}`,
        { method: removing ? "DELETE" : "PUT" },
      );
      if (response.status === 401) {
        window.location.assign("/account/reservations");
        return;
      }
      if (!response.ok) return;
      setAuthenticated(true);
      setFavorites((current) => {
        const next = new Set(current);
        if (removing) next.delete(locationSlug);
        else next.add(locationSlug);
        return next;
      });
    } finally {
      setPending(undefined);
    }
  };

  return {
    isFavorite: (locationSlug: string) => favorites.has(locationSlug),
    pending,
    toggle,
  };
}
